import React, { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    google?: any;
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

let scriptPromise: Promise<void> | null = null;
function loadGsi(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load Google sign-in'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

interface Props {
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  disabled?: boolean;
  onCredential: (credential: string) => void;
  onError?: (message: string) => void;
}

// Renders the official Google button; renders nothing if no client id is configured
function GoogleSignInButton({ text = 'continue_with', disabled = false, onCredential, onError }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onCredential);
  const errorRef = useRef(onError);
  const [ready, setReady] = useState(false);

  callbackRef.current = onCredential;
  errorRef.current = onError;

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;

    loadGsi()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resp: { credential?: string }) => {
            if (resp.credential) callbackRef.current(resp.credential);
            else errorRef.current?.('Google sign-in was cancelled');
          },
        });
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          width: Math.min(containerRef.current.offsetWidth || 320, 400),
        });
        setReady(true);
      })
      .catch((e) => errorRef.current?.(e.message));

    return () => {
      cancelled = true;
    };
  }, [text]);

  if (!CLIENT_ID) return null;

  return (
    <div
      className="oauth-google-wrapper"
      style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
      aria-busy={!ready}
    >
      <div ref={containerRef} className="oauth-google-button" />
    </div>
  );
}

export const isGoogleOAuthEnabled = !!CLIENT_ID;
export default GoogleSignInButton;
